import React from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  ButtonBase,
} from "@mui/material";
import { downloadContentFile } from "../api/backend";
import CloseIcon from "@mui/icons-material/Close";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CodeIcon from "@mui/icons-material/Code";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CheckIcon from "@mui/icons-material/Check";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NumbersIcon from "@mui/icons-material/Numbers";

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  ink: "var(--color-ink)",
  ink2: "rgba(31,22,51,0.75)",
  ink3: "rgba(31,22,51,0.6)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-hairline-cloud)",
  surface2: "var(--color-surface-press-light)",
  surface3: "var(--color-surface-press-stronger)",
  primary: "var(--color-primary)",
  p700: "color-mix(in srgb, var(--color-primary) 85%, black)",
  p50: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
  danger: "var(--color-danger)",
  amber: "var(--color-warn)",
};

// ─── Type registry ────────────────────────────────────────────────────────────
const TYPES = [
  {
    id: "link",
    label: "Link",
    desc: "External URL / web resource",
    Icon: LinkIcon,
    color: "var(--color-primary)",
    bg: "rgba(37,99,235,.10)",
    backendType: "LINK",
  },
  {
    id: "external_link",
    label: "External Link",
    desc: "One link for the class, or a unique private link per student",
    Icon: LinkIcon,
    color: "var(--color-accent-violet)",
    bg: "rgba(107,79,187,.10)",
    backendType: "EXTERNAL_LINK",
  },
  {
    id: "document",
    label: "Document",
    desc: "PDF, Word, slides",
    Icon: DescriptionOutlinedIcon,
    color: "#15803D",
    bg: "rgba(21,128,61,.10)",
    backendType: "DOCUMENT",
  },
  {
    id: "video",
    label: "Video",
    desc: "MP4, MOV, WEBM — hosted & streamed",
    Icon: PlayCircleOutlineIcon,
    color: "#B5651D",
    bg: "rgba(181,101,29,.10)",
    backendType: "VIDEO",
  },
  {
    id: "image",
    label: "Image",
    desc: "PNG, JPG, GIF",
    Icon: ImageOutlinedIcon,
    color: "var(--color-accent-violet)",
    bg: "rgba(14,124,123,.10)",
    backendType: "DOCUMENT",
  },
  {
    id: "embed",
    label: "Embed",
    desc: "iframe / embed code",
    Icon: CodeIcon,
    color: "var(--color-accent-violet)",
    bg: "rgba(107,79,187,.10)",
    backendType: "DOCUMENT",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const niceBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const getHostname = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

const detectVideo = (url) => {
  if (!url) return null;
  if (/youtube\.com\/watch\?v=|youtu\.be\//.test(url)) return "YouTube";
  if (/vimeo\.com\/\d+/.test(url)) return "Vimeo";
  if (/\.(mp4|webm|mov)$/i.test(url)) return "Direct";
  return null;
};

const fileExt = (name = "") => (name.split(".").pop() || "").toLowerCase();

const fileTagStyle = (ext) => {
  if (ext === "pdf") return { color: "#B42318", bg: "rgba(180,35,24,.10)", label: "PDF" };
  if (["doc", "docx"].includes(ext)) return { color: "var(--color-primary)", bg: "rgba(37,99,235,.10)", label: "DOC" };
  if (["ppt", "pptx", "key"].includes(ext)) return { color: "#B5651D", bg: "rgba(181,101,29,.10)", label: "PPT" };
  if (["xls", "xlsx", "csv"].includes(ext)) return { color: "#15803D", bg: "rgba(21,128,61,.10)", label: "XLS" };
  return { color: "rgba(31,22,51,0.75)", bg: "var(--color-surface-press-stronger)", label: ext ? ext.toUpperCase().slice(0, 4) : "FILE" };
};

const sessionLabel = (s) =>
  `${s.sessionNumber ? "Session " + s.sessionNumber : "Session " + s.id}${s.sessionDate ? " – " + s.sessionDate : ""}`;

// ─── Shared primitives ────────────────────────────────────────────────────────
const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: 12,
      fontWeight: 600,
      color: T.ink2,
      letterSpacing: ".02em",
      mb: "6px",
      display: "block",
    }}
  >
    {children}
  </Typography>
);

const Req = () => (
  <Box component="span" sx={{ color: T.danger }}>
    {" "}*
  </Box>
);

const inputSx = {
  width: "100%",
  display: "block",
  background: "var(--color-surface-canvas-light)",
  border: `1px solid ${T.line}`,
  borderRadius: "10px",
  p: "12px 14px",
  fontSize: 14,
  color: T.ink,
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
  resize: "none",
  lineHeight: 1.5,
  transition: "border-color .15s, box-shadow .15s",
  "&:focus": {
    borderColor: T.primary,
    boxShadow: "0 0 0 4px rgba(31,58,138,.10)",
  },
};

// ─── TypePicker ───────────────────────────────────────────────────────────────
function TypePicker({ value, onChange }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "8px" }}>
      {TYPES.map((t) => {
        const selected = value === t.id;
        return (
          <ButtonBase
            key={t.id}
            onClick={() => onChange(t.id)}
            sx={{
              p: "12px 10px",
              borderRadius: "10px",
              textAlign: "left",
              background: "var(--color-surface-canvas-light)",
              border: `1.5px solid ${selected ? t.color : T.line}`,
              boxShadow: selected ? `0 0 0 3px ${t.bg}` : "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "6px",
              transition: "all .12s ease",
              "&:hover": { borderColor: t.color },
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                background: t.bg,
                color: t.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <t.Icon sx={{ fontSize: 16 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.ink, lineHeight: 1.2 }}>
                {t.label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: T.ink3, mt: "2px", lineHeight: 1.3 }}>
                {t.desc}
              </Typography>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

// ─── FileDropZone ─────────────────────────────────────────────────────────────
function FileDropZone({ accept, file, onFile, hint }) {
  const inputRef = React.useRef(null);
  const [over, setOver] = React.useState(false);

  if (file) {
    const ext = fileExt(file.name);
    const tag = fileTagStyle(ext);
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          p: "14px 16px",
          borderRadius: "10px",
          background: "var(--color-surface-canvas-light)",
          border: `1px solid ${T.line}`,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 48,
            borderRadius: "7px",
            background: tag.bg,
            color: tag.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: ".02em",
            flexShrink: 0,
          }}
        >
          {tag.label}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: T.ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {file.name}
          </Typography>
          <Typography sx={{ fontSize: 12, color: T.ink3, mt: "2px" }}>
            {niceBytes(file.size)} · {ext.toUpperCase() || "file"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => onFile(null)} sx={{ color: T.ink3 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      onClick={() => inputRef.current?.click()}
      sx={{
        p: "24px 18px",
        borderRadius: "12px",
        border: `1.5px dashed ${over ? T.primary : T.line}`,
        background: over ? "rgba(31,58,138,.04)" : T.surface2,
        cursor: "pointer",
        transition: "all .12s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "11px",
          background: "var(--color-surface-canvas-light)",
          color: T.primary,
          border: `1px solid ${T.line}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CloudUploadOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>
      <Typography sx={{ fontSize: 14, color: T.ink, fontWeight: 600 }}>
        Drop a file here or{" "}
        <Box component="span" sx={{ color: T.primary }}>
          browse
        </Box>
      </Typography>
      <Typography sx={{ fontSize: 12, color: T.ink3 }}>{hint}</Typography>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
    </Box>
  );
}

// ─── SourceField ──────────────────────────────────────────────────────────────
function SourceField({ type, url, setUrl, file, setFile, embed, setEmbed, linkScope, setLinkScope }) {
  const hostname = getHostname(url);

  if (type === "external_link") {
    return (
      <Box>
        <FieldLabel>Link scope<Req /></FieldLabel>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            variant={linkScope === "CLASS_WIDE" ? "contained" : "outlined"}
            size="small"
            onClick={() => setLinkScope("CLASS_WIDE")}
          >
            Whole class
          </Button>
          <Button
            variant={linkScope === "PER_STUDENT" ? "contained" : "outlined"}
            size="small"
            onClick={() => setLinkScope("PER_STUDENT")}
          >
            Unique per student
          </Button>
        </Box>
        {linkScope === "PER_STUDENT" ? (
          <Typography sx={{ fontSize: 12, color: T.ink3 }}>
            Each student gets their own private link, set after you save this
            item. Nobody but that student can ever see it again — not you,
            not other staff.
          </Typography>
        ) : (
          <Box>
            <FieldLabel>URL<Req /></FieldLabel>
            <Box
              component="input"
              sx={inputSx}
              placeholder="https://www.merckmanuals.com/professional/…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </Box>
        )}
      </Box>
    );
  }

  if (type === "link") {
    return (
      <Box>
        <FieldLabel>URL<Req /></FieldLabel>
        <Box
          component="input"
          sx={inputSx}
          placeholder="https://www.merckmanuals.com/professional/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        {url && !hostname && (
          <Box
            sx={{
              mt: 1,
              p: "10px 12px",
              borderRadius: "9px",
              background: "rgba(180,35,24,.06)",
              border: "1px solid rgba(180,35,24,.18)",
              fontSize: 12,
              color: T.danger,
            }}
          >
            Not a valid URL yet.
          </Box>
        )}
        {hostname && (
          <Box
            sx={{
              mt: 1,
              p: "10px 12px",
              borderRadius: "9px",
              background: "var(--color-surface-canvas-light)",
              border: `1px solid ${T.line}`,
              display: "flex",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "7px",
                background: T.p50,
                color: T.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 13,
                flexShrink: 0,
              }}
            >
              {hostname[0]?.toUpperCase()}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: T.ink,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {hostname}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  color: T.ink3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {url}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                height: 22,
                px: 1,
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                background: T.surface3,
                color: T.ink2,
                whiteSpace: "nowrap",
              }}
            >
              <OpenInNewIcon sx={{ fontSize: 11 }} /> Opens in new tab
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  if (type === "document") {
    return (
      <Box>
        <FieldLabel>Document<Req /></FieldLabel>
        <FileDropZone
          file={file}
          onFile={setFile}
          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md"
          hint="PDF, Word, PowerPoint, Excel · up to 50 MB"
        />
      </Box>
    );
  }

  if (type === "image") {
    return (
      <Box>
        <FieldLabel>Image<Req /></FieldLabel>
        <FileDropZone
          file={file}
          onFile={setFile}
          accept="image/*"
          hint="PNG, JPG, GIF, WebP · up to 10 MB"
        />
      </Box>
    );
  }

  if (type === "video") {
    return (
      <Box>
        <FieldLabel>Video file<Req /></FieldLabel>
        <FileDropZone
          file={file}
          onFile={setFile}
          accept="video/*"
          hint="MP4, MOV, WEBM · up to 500 MB"
        />
      </Box>
    );
  }

  if (type === "embed") {
    return (
      <Box>
        <FieldLabel>Embed code<Req /></FieldLabel>
        <Box
          component="textarea"
          sx={{ ...inputSx, minHeight: 96, fontFamily: "monospace", fontSize: 12 }}
          placeholder={'<iframe src="https://…" width="560" height="315" allowfullscreen></iframe>'}
          value={embed}
          onChange={(e) => setEmbed(e.target.value)}
        />
        <Typography sx={{ fontSize: 12, color: T.ink3, mt: "6px" }}>
          Paste from YouTube, Google Slides, Canva, etc. Embed must be from a trusted source.
        </Typography>
      </Box>
    );
  }

  return null;
}

// ─── TagsInput ────────────────────────────────────────────────────────────────
function TagsInput({ tags, onChange }) {
  const [val, setVal] = React.useState("");

  const add = (t) => {
    const clean = t.trim();
    if (!clean || tags.includes(clean)) return;
    onChange([...tags, clean]);
    setVal("");
  };

  const remove = (t) => onChange(tags.filter((x) => x !== t));

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "6px",
        p: "8px 10px",
        background: "var(--color-surface-canvas-light)",
        border: `1px solid ${T.line}`,
        borderRadius: "10px",
        minHeight: 46,
      }}
    >
      {tags.map((t) => (
        <Box
          key={t}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            height: 24,
            px: "8px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            background: T.p50,
            color: T.primary,
          }}
        >
          {t}
          <IconButton
            size="small"
            onClick={() => remove(t)}
            sx={{ p: 0, color: "inherit", ml: "2px", "& svg": { fontSize: 12 } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      ))}
      <Box
        component="input"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === ",") && val.trim()) {
            e.preventDefault();
            add(val);
          } else if (e.key === "Backspace" && !val && tags.length) {
            onChange(tags.slice(0, -1));
          }
        }}
        placeholder={tags.length === 0 ? "Add tags (press Enter)…" : ""}
        sx={{
          flex: 1,
          minWidth: 120,
          border: "none",
          outline: "none",
          fontSize: 13,
          color: T.ink,
          fontFamily: "inherit",
          background: "transparent",
        }}
      />
    </Box>
  );
}

// ─── VisibilityPicker ─────────────────────────────────────────────────────────
function VisibilityPicker({ visibility, setVisibility, attached, setAttached, sessions }) {
  const allSelected = sessions.length > 0 && attached.length === sessions.length;

  const toggle = (id) => {
    const next = attached.includes(String(id))
      ? attached.filter((x) => x !== String(id))
      : [...attached, String(id)];
    setAttached(next);
  };

  const RadioCard = ({ val, label, desc }) => {
    const on = visibility === val;
    return (
      <ButtonBase
        onClick={() => setVisibility(val)}
        sx={{
          p: "12px 14px",
          borderRadius: "9px",
          textAlign: "left",
          background: "var(--color-surface-canvas-light)",
          border: `1.5px solid ${on ? T.primary : T.line}`,
          boxShadow: on ? "0 0 0 3px rgba(31,58,138,.06)" : "none",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          transition: "all .12s ease",
          "&:hover": { borderColor: T.primary },
        }}
      >
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            border: `1.5px solid ${on ? T.primary : "var(--color-hairline-cool)"}`,
            background: on ? T.primary : "transparent",
            position: "relative",
            flexShrink: 0,
          }}
        >
          {on && (
            <Box
              sx={{
                position: "absolute",
                inset: 3,
                background: "var(--color-surface-canvas-light)",
                borderRadius: "50%",
              }}
            />
          )}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 11, color: T.ink3 }}>{desc}</Typography>
        </Box>
      </ButtonBase>
    );
  };

  return (
    <Box>
      <FieldLabel>Visibility</FieldLabel>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
        <RadioCard val="all" label="All students in class" desc="Visible from day one" />
        <RadioCard val="sessions" label="Specific sessions" desc="Unlocks with sessions" />
      </Box>

      {visibility === "sessions" && (
        <Box
          sx={{
            mt: "10px",
            border: `1px solid ${T.line}`,
            borderRadius: "10px",
            overflow: "hidden",
            background: "var(--color-surface-canvas-light)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "12px",
              py: "8px",
              borderBottom: `1px solid ${T.line2}`,
              background: T.surface2,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: T.ink3,
                letterSpacing: ".08em",
                textTransform: "uppercase",
              }}
            >
              Attach to sessions ({attached.length}/{sessions.length})
            </Typography>
            <ButtonBase
              onClick={() =>
                setAttached(
                  allSelected ? [] : sessions.map((s) => String(s.id))
                )
              }
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: T.primary,
                fontFamily: "inherit",
                borderRadius: "4px",
                px: "4px",
                py: "2px",
              }}
            >
              {allSelected ? "Clear" : "Select all"}
            </ButtonBase>
          </Box>
          <Box sx={{ maxHeight: 158, overflowY: "auto" }}>
            {sessions.map((s) => {
              const on = attached.includes(String(s.id));
              return (
                <ButtonBase
                  key={s.id}
                  component="label"
                  onClick={() => toggle(s.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    px: "12px",
                    py: "9px",
                    width: "100%",
                    cursor: "pointer",
                    borderBottom: `1px solid ${T.line2}`,
                    background: on ? T.p50 : "#fff",
                    textAlign: "left",
                    "&:last-child": { borderBottom: "none" },
                    transition: "background .1s",
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "4px",
                      border: `1.5px solid ${on ? T.primary : "var(--color-hairline-cool)"}`,
                      background: on ? T.primary : "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      color: "#fff",
                    }}
                  >
                    {on && <CheckIcon sx={{ fontSize: 11 }} />}
                  </Box>
                  <Typography sx={{ flex: 1, fontSize: 13, color: T.ink }}>
                    {sessionLabel(s)}
                  </Typography>
                  {s.sessionDate && (
                    <Typography sx={{ fontSize: 11, color: T.ink3 }}>
                      {s.sessionDate}
                    </Typography>
                  )}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ─── ContentCardPreview ───────────────────────────────────────────────────────
function ContentCardPreview({ type, title, description, url, file, embed, tags, required, featured, imageSrc }) {
  const t = TYPES.find((x) => x.id === type);
  const hostname = getHostname(url);
  const videoProvider = detectVideo(url);
  const ext = file ? fileExt(file.name) : "";
  const ftag = ext ? fileTagStyle(ext) : null;

  const Thumb = () => {
    if (imageSrc && (type === "image" || type === "document")) {
      return (
        <Box
          sx={{
            aspectRatio: "16/9",
            borderRadius: "10px",
            background: T.surface3,
            border: `1px solid ${T.line}`,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img src={imageSrc} alt={title || "Preview"} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        </Box>
      );
    }
    if (type === "link") {
      return (
        <Box
          sx={{
            aspectRatio: "16/8",
            borderRadius: "10px",
            background: "linear-gradient(135deg, var(--color-primary-10), #DDE3F2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            border: `1px solid ${T.line}`,
          }}
        >
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: "13px",
              background: "var(--color-surface-canvas-light)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              fontWeight: 800,
              color: T.primary,
            }}
          >
            {hostname ? hostname[0]?.toUpperCase() : <LinkIcon />}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
              {hostname || "External link"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: T.ink3 }}>Opens in a new tab</Typography>
          </Box>
        </Box>
      );
    }
    if (type === "document") {
      return (
        <Box
          sx={{
            aspectRatio: "16/8",
            borderRadius: "10px",
            background: ftag?.bg || T.surface3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            border: `1px solid ${ftag ? ftag.color + "30" : T.line}`,
          }}
        >
          <Box
            sx={{
              width: 54,
              height: 64,
              borderRadius: "7px",
              background: "var(--color-surface-canvas-light)",
              color: ftag?.color || T.ink2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: ".04em",
              boxShadow: "0 2px 6px rgba(0,0,0,.08)",
            }}
          >
            {ftag?.label || "FILE"}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
              {file ? file.name : "No file selected"}
            </Typography>
            <Typography sx={{ fontSize: 11, color: T.ink3 }}>
              {file ? `${niceBytes(file.size)} · click to open` : "Add a file to see preview"}
            </Typography>
          </Box>
        </Box>
      );
    }
    if (type === "video") {
      return (
        <Box
          sx={{
            aspectRatio: "16/9",
            borderRadius: "10px",
            background: "#0F1117",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            position: "relative",
            overflow: "hidden",
            border: `1px solid ${T.line}`,
          }}
        >
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: "50%",
              background: "rgba(255,255,255,.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
            }}
          >
            ▶
          </Box>
          {videoProvider && (
            <Box
              sx={{
                position: "absolute",
                bottom: 8,
                left: 10,
                fontSize: 11,
                color: "rgba(255,255,255,.7)",
              }}
            >
              {videoProvider}
            </Box>
          )}
        </Box>
      );
    }
    if (type === "image") {
      return (
        <Box
          sx={{
            aspectRatio: "16/9",
            borderRadius: "10px",
            background: T.surface3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: T.ink3,
            border: `1px solid ${T.line}`,
          }}
        >
          {file ? (
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{file.name}</Typography>
          ) : (
            <ImageOutlinedIcon />
          )}
        </Box>
      );
    }
    if (type === "embed") {
      return (
        <Box
          sx={{
            aspectRatio: "16/9",
            borderRadius: "10px",
            background: T.surface3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: T.ink3,
            border: `1.5px dashed ${T.line}`,
            gap: "6px",
          }}
        >
          <CodeIcon />
          <Typography sx={{ fontSize: 12 }}>
            {embed ? "Embed will render here" : "Paste embed code to preview"}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      sx={{
        background: "var(--color-surface-canvas-light)",
        border: `1px solid ${T.line}`,
        borderRadius: "14px",
        p: 2,
        boxShadow: "0 1px 0 rgba(0,0,0,.03)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "10px" }}>
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "6px",
            background: t.bg,
            color: t.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <t.Icon sx={{ fontSize: 13 }} />
        </Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: t.color,
            letterSpacing: ".08em",
            textTransform: "uppercase",
          }}
        >
          {t.label}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {required && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              height: 22,
              px: "8px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              background: "rgba(180,35,24,.10)",
              color: T.danger,
            }}
          >
            Required
          </Box>
        )}
        {featured && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              height: 22,
              px: "8px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              background: "rgba(181,101,29,.10)",
              color: "var(--color-warn)",
            }}
          >
            <StarOutlineIcon sx={{ fontSize: 12 }} />Featured
          </Box>
        )}
      </Box>

      <Thumb />

      <Box sx={{ mt: "12px" }}>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 700,
            color: title.trim() ? T.ink : "var(--color-ink-4)",
            lineHeight: 1.3,
            fontFamily: "var(--font-display)",
          }}
        >
          {title.trim() || "Untitled content"}
        </Typography>
        {description.trim() && (
          <Typography sx={{ fontSize: 13, color: T.ink2, mt: "4px", lineHeight: 1.45 }}>
            {description}
          </Typography>
        )}
        {tags.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: "10px" }}>
            {tags.map((tag) => (
              <Box
                key={tag}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  height: 22,
                  px: "8px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 600,
                  background: T.surface3,
                  color: T.ink2,
                }}
              >
                <LocalOfferIcon sx={{ fontSize: 11 }} />
                {tag}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  type: "link",
  title: "",
  description: "",
  url: "",
  file: null,
  existingFile: null,
  embed: "",
  tags: [],
  required: false,
  featured: false,
  instructorViewOnly: false,
  visibility: "all",
  attachedSessions: [],
  linkScope: "CLASS_WIDE",
};

const IMAGE_RE = /\.(png|jpe?g|gif|webp|svg)$/i;
const VIDEO_RE = /\.(mp4|mov|avi|webm)$/i;

function typeForFileName(name) {
  if (IMAGE_RE.test(name || "")) return "image";
  if (VIDEO_RE.test(name || "")) return "video";
  return "document";
}

const BACKEND_TO_FRONT = {
  LINK: "link",
  EXTERNAL_LINK: "external_link",
  DOCUMENT: "document",
  VIDEO: "video",
  IMAGE: "image",
  EMBED: "embed",
};

export default function AddClassContentModal({
  open,
  onClose,
  onSave,
  sessions = [],
  editTarget = null,
  dialogTarget = "class",
  saving = false,
  saveError = "",
  prefillFile = null,
  titleOverride = null,
}) {
  const [form, setForm] = React.useState(EMPTY_FORM);

  React.useEffect(() => {
    if (!open) return;
    if (editTarget) {
      const parseTags = (raw) => {
        if (Array.isArray(raw)) return raw;
        try { const p = JSON.parse(raw || "[]"); return Array.isArray(p) ? p : []; } catch { return []; }
      };
      const parseAttached = (raw) => {
        if (Array.isArray(raw)) return raw.map(String);
        try { const p = JSON.parse(raw || "[]"); return Array.isArray(p) ? p.map(String) : []; } catch { return []; }
      };
      const attached = parseAttached(editTarget.attachedSessionIds || editTarget.attachedSessions);
      setForm({
        ...EMPTY_FORM,
        type:
          editTarget.contentType === "DOCUMENT"
            ? typeForFileName(editTarget.fileName)
            : BACKEND_TO_FRONT[editTarget.contentType] || "link",
        title: editTarget.title || "",
        description: editTarget.description || "",
        url: editTarget.url || "",
        embed: editTarget.embedCode || "",
        tags: parseTags(editTarget.tags),
        required: editTarget.required || false,
        featured: editTarget.featured || false,
        instructorViewOnly: editTarget.instructorViewOnly || false,
        visibility: attached.length > 0 ? "sessions" : "all",
        attachedSessions: attached,
        linkScope: editTarget.linkScope || "CLASS_WIDE",
        existingFile: editTarget.hasFile
          ? { name: editTarget.fileName || editTarget.title, size: editTarget.fileSize || 0 }
          : null,
      });
    } else if (prefillFile) {
      setForm({ ...EMPTY_FORM, type: typeForFileName(prefillFile.name), file: prefillFile });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, editTarget, prefillFile]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [imageSrc, setImageSrc] = React.useState(null);
  React.useEffect(() => {
    if (!open) return undefined;
    let revoke = null;
    let cancelled = false;
    setImageSrc(null);
    if (form.file) {
      if (IMAGE_RE.test(form.file.name || "") || (form.file.type || "").startsWith("image/")) {
        revoke = URL.createObjectURL(form.file);
        setImageSrc(revoke);
      }
    } else if (form.existingFile && IMAGE_RE.test(form.existingFile.name || "")) {
      downloadContentFile(editTarget.id)
        .then((u) => { if (!cancelled) { revoke = u; setImageSrc(u); } })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [open, form.file, form.existingFile, editTarget]);

  const displayFile = form.file || form.existingFile;
  const typeObj = TYPES.find((t) => t.id === form.type);
  const isClassLevel = dialogTarget === "class";

  const sourceOk =
    (form.type === "link" && !!getHostname(form.url)) ||
    (form.type === "external_link" &&
      (form.linkScope === "PER_STUDENT" || !!getHostname(form.url))) ||
    ((form.type === "document" || form.type === "image" || form.type === "video") &&
      !!displayFile) ||
    (form.type === "embed" && form.embed.trim().length > 0);

  const canSave = !!form.title.trim() && sourceOk;

  const sizeLabel =
    (form.type === "document" || form.type === "image" || form.type === "video") && displayFile
      ? niceBytes(displayFile.size)
      : "—";

  const sessionsLabel =
    form.visibility === "all"
      ? "All students"
      : `${form.attachedSessions.length} session${form.attachedSessions.length === 1 ? "" : "s"}`;

  const modalTitle = editTarget
    ? "Edit content item"
    : titleOverride
    ? titleOverride
    : isClassLevel
    ? "Add class-level content"
    : `Add content to session`;

  const statusText = canSave
    ? `Ready · ${typeObj.label.toLowerCase()} for ${
        isClassLevel
          ? form.visibility === "all"
            ? "all students"
            : `${form.attachedSessions.length} sessions`
          : "this session"
      }`
    : `Add a title and ${
        form.type === "link" ||
        (form.type === "external_link" && form.linkScope === "CLASS_WIDE")
          ? "a valid URL"
          : form.type === "external_link"
          ? "student scope"
          : form.type === "embed"
          ? "embed code"
          : "a file"
      } to continue`;

  const handleSubmit = () => {
    if (!canSave) return;
    onSave({
      title: form.title.trim(),
      contentType: typeObj.backendType,
      description: form.description.trim(),
      url:
        form.type === "link" ||
        (form.type === "external_link" && form.linkScope === "CLASS_WIDE")
          ? form.url
          : "",
      linkScope: form.type === "external_link" ? form.linkScope : undefined,
      embedCode: form.type === "embed" ? form.embed : "",
      tags: form.tags,
      required: form.required,
      featured: form.featured,
      instructorViewOnly: form.instructorViewOnly,
      visibility: isClassLevel ? form.visibility : "sessions",
      attachedSessions: isClassLevel ? form.attachedSessions : [],
      file: form.file,
    });
  };

  // Summary tiles config
  const summaryTiles = [
    {
      icon: <typeObj.Icon sx={{ fontSize: 14, color: typeObj.color }} />,
      label: "Content type",
      value: typeObj.label,
      mono: false,
    },
    {
      icon: <NumbersIcon sx={{ fontSize: 14, color: "var(--color-accent-violet)" }} />,
      label: "Size",
      value: sizeLabel,
      mono: true,
    },
    {
      icon: <FormatListBulletedIcon sx={{ fontSize: 14, color: "var(--color-primary)" }} />,
      label: "Visibility",
      value: sessionsLabel,
      mono: false,
    },
    {
      icon: <LocalOfferIcon sx={{ fontSize: 14, color: "#B5651D" }} />,
      label: "Tags",
      value: String(form.tags.length),
      mono: true,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(1180px, calc(100vw - 48px))",
          maxHeight: "min(920px, calc(100vh - 48px))",
          borderRadius: "18px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          m: 0,
          boxShadow:
            "0 24px 60px -20px rgba(20,24,40,.28), 0 12px 24px -16px rgba(20,24,40,.18)",
          border: "1px solid rgba(0,0,0,.04)",
        },
      }}
    >
      {/* ── HEADER ── */}
      <Box
        sx={{
          px: 3,
          py: "18px",
          borderBottom: `1px solid ${T.line2}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: T.p50,
              color: T.primary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FolderOpenIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: T.ink, fontFamily: "var(--font-display)" }}>
              {modalTitle}
            </Typography>
            <Typography sx={{ fontSize: 12, color: T.ink3 }}>
              {form.title.trim() || "Untitled"} · {typeObj.label.toLowerCase()}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              height: 24,
              px: "10px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              background: typeObj.bg,
              color: typeObj.color,
            }}
          >
            <typeObj.Icon sx={{ fontSize: 13 }} />
            {typeObj.label}
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ width: 36, height: 36, borderRadius: "10px", color: T.ink2 }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── SUMMARY STRIP ── */}
      <Box
        sx={{
          px: 3,
          py: "14px",
          borderBottom: `1px solid ${T.line2}`,
          background: "var(--color-surface-2)",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "10px",
          }}
        >
          {summaryTiles.map(({ icon, label, value, mono }) => (
            <Box
              key={label}
              sx={{
                background: "var(--color-surface-canvas-light)",
                borderRadius: "10px",
                p: "10px 12px",
                border: `1px solid ${T.line2}`,
                boxShadow: "var(--shadow-card-sm)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: 11,
                  color: T.ink3,
                  fontWeight: 600,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                }}
              >
                {icon}
                {label}
              </Box>
              <Typography
                sx={{
                  fontSize: mono ? 20 : 16,
                  fontWeight: 700,
                  color: T.ink,
                  mt: "2px",
                  fontVariantNumeric: "tabular-nums",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontFamily: "var(--font-display)",
                }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── BODY ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1.1fr 1fr",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Form column */}
        <Box
          sx={{
            px: 3,
            py: "22px",
            borderRight: `1px solid ${T.line2}`,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          {/* Type picker */}
          <Box>
            <FieldLabel>Content type<Req /></FieldLabel>
            <TypePicker value={form.type} onChange={(v) => upd("type", v)} />
          </Box>

          {/* Source field */}
          <SourceField
            type={form.type}
            linkScope={form.linkScope}
            setLinkScope={(v) => upd("linkScope", v)}
            url={form.url}
            setUrl={(v) => upd("url", v)}
            file={displayFile}
            setFile={(v) => setForm((f) => ({ ...f, file: v, existingFile: null }))}
            embed={form.embed}
            setEmbed={(v) => upd("embed", v)}
          />

          {/* Title */}
          <Box>
            <FieldLabel>Title<Req /></FieldLabel>
            <Box
              component="input"
              sx={inputSx}
              placeholder={
                form.type === "link"
                  ? "e.g. AHA CPR guidelines (2025 update)"
                  : "e.g. Patient assessment quick-reference"
              }
              value={form.title}
              onChange={(e) => upd("title", e.target.value)}
            />
          </Box>

          {/* Description */}
          <Box>
            <FieldLabel>Description</FieldLabel>
            <Box
              component="textarea"
              sx={{ ...inputSx, minHeight: 78 }}
              placeholder="Short context for students — what this is and how to use it."
              value={form.description}
              onChange={(e) => upd("description", e.target.value)}
            />
          </Box>

          {/* Tags */}
          <Box>
            <FieldLabel>Tags</FieldLabel>
            <TagsInput tags={form.tags} onChange={(v) => upd("tags", v)} />
            <Typography sx={{ fontSize: 12, color: T.ink3, mt: "6px" }}>
              Press Enter or comma to add. Used for search and filtering.
            </Typography>
          </Box>

          {/* Flags */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            {[
              {
                key: "required",
                label: "Required reading",
                desc: "Counted in completion tracking",
                color: T.danger,
              },
              {
                key: "featured",
                label: "Featured",
                desc: "Pin to top of class resources",
                color: T.amber,
              },
              {
                key: "instructorViewOnly",
                label: "Instructor view only",
                desc: "Hidden from students. Assigned instructors can open it",
                color: T.primary,
                wide: true,
              },
            ].map(({ key, label, desc, color, wide }) => {
              const on = form[key];
              return (
                <ButtonBase
                  key={key}
                  data-testid={key === "instructorViewOnly" ? "instructor-view-only" : undefined}
                  aria-pressed={on}
                  onClick={() => upd(key, !on)}
                  sx={{
                    p: "12px 14px",
                    borderRadius: "10px",
                    textAlign: "left",
                    background: "var(--color-surface-canvas-light)",
                    border: `1.5px solid ${on ? color : T.line}`,
                    boxShadow: on ? `0 0 0 3px color-mix(in srgb, ${color} 18%, transparent)` : "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    transition: "all .12s ease",
                    gridColumn: wide ? "1 / -1" : undefined,
                    "&:hover": { borderColor: color },
                  }}
                >
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: "5px",
                      border: `1.5px solid ${on ? color : "var(--color-hairline-cool)"}`,
                      background: on ? color : "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      flexShrink: 0,
                    }}
                  >
                    {on && <CheckIcon sx={{ fontSize: 12 }} />}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: T.ink3 }}>{desc}</Typography>
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>

          {/* Visibility (class-level only) */}
          {isClassLevel && (
            <VisibilityPicker
              visibility={form.visibility}
              setVisibility={(v) => upd("visibility", v)}
              attached={form.attachedSessions}
              setAttached={(v) => upd("attachedSessions", v)}
              sessions={sessions}
            />
          )}
        </Box>

        {/* Preview column */}
        <Box
          sx={{
            background: "var(--color-surface-2)",
            px: 3,
            py: "22px",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: "12px",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".1em",
                color: T.ink3,
                textTransform: "uppercase",
              }}
            >
              {form.instructorViewOnly ? "Hidden from students" : "Student view · preview"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: 11,
                color: T.ink3,
              }}
            >
              <VisibilityOutlinedIcon sx={{ fontSize: 14 }} /> Live
            </Box>
          </Box>

          <ContentCardPreview
            type={form.type}
            title={form.title}
            description={form.description}
            url={form.url}
            file={displayFile}
            embed={form.embed}
            tags={form.tags}
            required={form.required}
            featured={form.featured}
            imageSrc={imageSrc}
          />

          {/* Session timeline preview */}
          {isClassLevel &&
            form.visibility === "sessions" &&
            form.attachedSessions.length > 0 && (
              <Box sx={{ mt: "14px" }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: ".1em",
                    color: T.ink3,
                    textTransform: "uppercase",
                    mb: "8px",
                  }}
                >
                  Where it appears
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {sessions
                    .filter((s) => form.attachedSessions.includes(String(s.id)))
                    .map((s) => (
                      <Box
                        key={s.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          p: "8px 12px",
                          background: "var(--color-surface-canvas-light)",
                          border: `1px solid ${T.line}`,
                          borderRadius: "9px",
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "6px",
                            background: typeObj.bg,
                            color: typeObj.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <typeObj.Icon sx={{ fontSize: 13 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12, color: T.ink3 }}>
                            {sessionLabel(s)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: T.ink,
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {form.title.trim() || "Untitled content"}
                          </Typography>
                        </Box>
                        {s.sessionDate && (
                          <Typography sx={{ fontSize: 11, color: T.ink3 }}>
                            {s.sessionDate}
                          </Typography>
                        )}
                      </Box>
                    ))}
                </Box>
              </Box>
            )}
        </Box>
      </Box>

      {/* ── FOOTER ── */}
      <Box
        sx={{
          px: 3,
          py: "14px",
          borderTop: `1px solid ${T.line2}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          background: "var(--color-surface-canvas-light)",
        }}
      >
        {saveError
          ? <Typography sx={{ fontSize: 12, color: "#d32f2f", fontWeight: 500 }}>{saveError}</Typography>
          : <Typography sx={{ fontSize: 12, color: T.ink3 }}>{statusText}</Typography>
        }
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            onClick={onClose}
            sx={{
              height: 42,
              px: "18px",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: 14,
              color: T.ink2,
              "&:hover": { background: T.surface3 },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSave || saving}
            sx={{
              height: 42,
              px: "18px",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: 14,
              background: T.primary,
              "&:hover": { background: T.p700 },
              "&:disabled": { opacity: 0.5, background: T.primary, color: "#fff" },
            }}
          >
            {saving ? "Saving…" : editTarget ? "Save changes" : "Add to class"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
